import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FiCheckCircle } from 'react-icons/fi';
import companyLogo from '../image/SMAC-X LOGO.jpg';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 bg-yellow-50 items-center justify-center border-r border-yellow-200">
        <div className="p-12 w-full h-full flex flex-col items-center justify-center">
          {/* Main Illustration placeholder - the user can place their image at /illustration.png in the public folder */}
          <div className="relative w-full max-w-lg aspect-square flex items-center justify-center">
            {/* We use the image from public folder. Replace illustration.png with the actual image file. */}
            <img 
              src={process.env.PUBLIC_URL + '/illustration.png'} 
              alt="Data extraction illustration" 
              className="w-full h-auto object-contain drop-shadow-md z-10"
            />
            {/* Subtle yellow backdrop glow behind the image */}
            <div className="absolute inset-0 bg-yellow-400 opacity-10 blur-3xl rounded-full transform scale-90"></div>
          </div>
          
          <h2 className="mt-12 text-3xl font-bold text-gray-800 text-center tracking-tight">
            Data insights, <span className="text-yellow-600">simplified.</span>
          </h2>
          <p className="mt-4 text-gray-500 text-center max-w-sm">
            Harness the power of AI to extract, analyze, and visualize your documents effortlessly.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-8 sm:p-12 lg:p-24 bg-white relative">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center justify-center mb-10">
            <img src={companyLogo} alt="SMAC-X Logo" className="h-16 w-auto mb-2 object-contain" />
          </div>

          {/* Desktop Logo (Top of form) */}
          <div className="hidden lg:block mb-10">
            <img src={companyLogo} alt="SMAC-X Logo" className="h-14 w-auto object-contain" />
          </div>

          <div className="mb-12 text-center lg:text-left">
            <h2 className="text-5xl font-light text-gray-800 mb-2 tracking-wider">LOGIN</h2>
            <div className="w-16 h-1 bg-yellow-500 mx-auto lg:mx-0 rounded-full mt-4 opacity-80"></div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 shadow-sm">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}
            
            {/* Email Input */}
            <div className="relative group">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-focus-within:text-yellow-500">
                Username
              </label>
              <div className="flex items-center border-b-2 border-gray-200 focus-within:border-yellow-500 transition-colors py-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-none focus:ring-0 text-gray-800 placeholder-gray-300 font-medium text-lg px-0 outline-none"
                  placeholder="Enter your email"
                />
                {email && <FiCheckCircle className="text-yellow-400 w-5 h-5 ml-2 transition-opacity duration-300" />}
              </div>
            </div>
            
            {/* Password Input */}
            <div className="relative group">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 transition-colors group-focus-within:text-yellow-500">
                Password
              </label>
              <div className="flex items-center border-b-2 border-gray-200 focus-within:border-yellow-500 transition-colors py-2">
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-none focus:ring-0 text-gray-800 placeholder-gray-300 font-medium text-lg px-0 outline-none tracking-widest"
                  placeholder="••••••••"
                />
                {password && <FiCheckCircle className="text-yellow-400 w-5 h-5 ml-2 transition-opacity duration-300" />}
              </div>
            </div>
            
            {/* Remember / Actions */}
            <div className="flex items-center text-sm pt-2">
              <label className="flex items-center text-gray-400 cursor-pointer hover:text-gray-600 transition-colors">
                <input type="checkbox" className="w-4 h-4 text-yellow-500 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 mr-3 cursor-pointer" />
                <span className="font-medium">Agree to remember the password</span>
              </label>
            </div>
            
            {/* Submit */}
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-8 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="bg-yellow-500 text-white min-w-[140px] py-3 px-8 rounded-full hover:bg-yellow-600 hover:shadow-lg hover:shadow-yellow-500/30 focus:ring-4 focus:ring-yellow-100 disabled:opacity-50 transition-all duration-300 font-bold tracking-wider text-sm flex items-center justify-center transform hover:-translate-y-0.5 uppercase"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  'SIGN IN'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;